/**
 * Bundle Selector - Variant-Based
 * Handles bundle variant selection and price updates
 * Now uses product variants instead of quantity manipulation
 */

class BundleSelector {
  constructor() {
    this.currentVariantId = null;
    this.currentQuantity = 1;
    this.currentPaidQuantity = 1;
    this.variantData = null;
    this.sectionId = null;
    this.moneyFormat = '${{amount}}';
    this.isUpdating = false;
    
    console.log('✓ Bundle selector initializing...');
    this.init();
  }

  init() {
    const bundleSelector = document.querySelector('.bundle-selector');
    const bundleOptions = document.querySelectorAll('.bundle-option__radio');
    
    if (bundleOptions.length === 0) {
      console.log('⚠️ No bundle options found');
      return;
    }

    // Get section ID and variant data
    if (bundleSelector) {
      this.sectionId = bundleSelector.dataset.sectionId;
      
      // Get variant data from window object
      if (window.bundleVariantData && window.bundleVariantData[this.sectionId]) {
        this.variantData = window.bundleVariantData[this.sectionId];
        console.log('✓ Variant data loaded:', this.variantData);
      }
    }

    // Get Shopify money format if available
    if (window.Shopify && window.Shopify.money_format) {
      this.moneyFormat = window.Shopify.money_format;
    }

    // Bind change events
    bundleOptions.forEach(radio => {
      radio.addEventListener('change', (e) => {
        if (this.isUpdating) {
          console.log('⏸️ Update in progress, skipping...');
          return;
        }
        
        this.isUpdating = true;
        const variantId = parseInt(e.target.dataset.variantId, 10);
        const totalQuantity = parseInt(e.target.dataset.quantity, 10);
        const paidQuantity = parseInt(e.target.dataset.paidQuantity || e.target.dataset.quantity, 10);
        
        this.currentVariantId = variantId;
        this.currentQuantity = totalQuantity;
        this.currentPaidQuantity = paidQuantity;
        
        console.log('Bundle variant changed:', {
          variantId: variantId,
          totalQuantity: totalQuantity,
          paidQuantity: paidQuantity
        });
        
        this.selectVariant(variantId, totalQuantity, paidQuantity);
        
        // Shorter delay to prevent cart interaction issues
        setTimeout(() => {
          this.isUpdating = false;
        }, 50);
      });
    });

    // Set initial variant based on checked option
    const checkedOption = document.querySelector('.bundle-option__radio:checked');
    if (checkedOption) {
      const initialVariantId = parseInt(checkedOption.dataset.variantId, 10);
      const initialQuantity = parseInt(checkedOption.dataset.quantity, 10);
      const initialPaidQuantity = parseInt(checkedOption.dataset.paidQuantity || checkedOption.dataset.quantity, 10);
      
      this.currentVariantId = initialVariantId;
      this.currentQuantity = initialQuantity;
      this.currentPaidQuantity = initialPaidQuantity;
      
      console.log('Initial variant:', { 
        variantId: initialVariantId, 
        quantity: initialQuantity,
        paidQuantity: initialPaidQuantity 
      });
      
      // Trigger initial variant selection to dispatch events for subscription widget
      // This ensures prices are set and events are fired on page load
      setTimeout(() => {
        this.selectVariant(initialVariantId, initialQuantity, initialPaidQuantity);
      }, 50);
    }

    console.log('✓ Bundle selector ready');
  }

  selectVariant(variantId, totalQuantity, paidQuantity) {
    // Find the product form
    const productForm = document.querySelector('product-form form[action*="/cart/add"]');
    
    if (!productForm) {
      console.warn('⚠️ Product form not found');
      return;
    }

    // 1. Update the variant ID input (only if changed to avoid triggering events)
    let variantInput = productForm.querySelector('input[name="id"]');
    if (variantInput) {
      if (variantInput.value !== String(variantId)) {
        variantInput.value = variantId;
        console.log('✓ Variant ID set to:', variantId);
      }
    } else {
      console.warn('⚠️ Variant input not found');
    }

    // 2. Update quantity (variants contain the quantity, but we set it to 1 since the variant IS the bundle)
    // The variant itself represents X units, so we add quantity=1 to cart
    let quantityInput = productForm.querySelector('input[name="quantity"]');
    if (!quantityInput) {
      quantityInput = document.createElement('input');
      quantityInput.type = 'hidden';
      quantityInput.name = 'quantity';
      productForm.appendChild(quantityInput);
    }
    // Only update if different to prevent form change events
    if (quantityInput.value !== '1') {
      quantityInput.value = '1'; // Always 1 - the variant represents the bundle
    }

    // 3. Update prices in the UI
    this.updatePricesForVariant(variantId, totalQuantity, paidQuantity);

    // 4. Dispatch variant change event for subscription widget
    document.dispatchEvent(new CustomEvent('variant:changed', {
      detail: {
        variantId: variantId,
        quantity: totalQuantity,
        paidQuantity: paidQuantity,
        variant: this.getVariantData(variantId)
      }
    }));

    console.log('✓ Variant selected and events dispatched');
  }

  getVariantData(variantId) {
    if (!this.variantData || !this.variantData.variants) return null;
    
    // Find variant by ID in our variant data
    for (const [qty, variant] of Object.entries(this.variantData.variants)) {
      if (variant.id === variantId) {
        return variant;
      }
    }
    return null;
  }

  updatePricesForVariant(variantId, totalQuantity, paidQuantity) {
    const variant = this.getVariantData(variantId);
    
    if (!variant) {
      console.warn('⚠️ Variant data not found for ID:', variantId);
      return;
    }

    const variantPrice = variant.price;
    
    // Check if subscription is active
    const subscriptionCheckbox = document.querySelector('.subscription-widget__checkbox');
    const isSubscriptionActive = subscriptionCheckbox && subscriptionCheckbox.checked;
    
    // Calculate the price to display (subscription discount if active)
    let displayPrice = variantPrice;
    if (isSubscriptionActive) {
      const discount = parseInt(subscriptionCheckbox.dataset.discount || '25') / 100;
      displayPrice = Math.round(variantPrice * (1 - discount));
      console.log(`Subscription active: applying ${discount * 100}% discount`);
    }
    
    const formattedPrice = this.formatMoney(displayPrice);

    console.log(`Updating prices for variant: ${formattedPrice} (${totalQuantity} items, paying for ${paidQuantity})`);

    // Update price in ATC button
    const priceElements = document.querySelectorAll('.product-form__submit .price-item--regular, .product-form__submit .price-item--sale');
    
    let updated = 0;
    priceElements.forEach(element => {
      // Skip strikethrough prices
      if (element.tagName === 'S' || element.closest('s')) return;
      
      element.dataset.variantId = variantId;
      element.dataset.bundleQuantity = totalQuantity;
      element.dataset.paidQuantity = paidQuantity;
      element.dataset.originalPrice = variantPrice; // Store original price for subscription calculations
      element.textContent = formattedPrice;
      updated++;
    });

    console.log(`✓ Updated ${updated} price elements to ${formattedPrice}`);

    // Dispatch event for subscription widget
    document.dispatchEvent(new CustomEvent('bundle:price-updated', {
      detail: { 
        variantId: variantId,
        quantity: totalQuantity,
        paidQuantity: paidQuantity,
        basePrice: variantPrice,
        totalPrice: variantPrice,
        variant: variant,
        isSubscriptionActive: isSubscriptionActive
      }
    }));

    console.log('✓ Dispatched bundle:price-updated event');
  }

  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = parseInt(cents, 10);
    }

    // Use Shopify's money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, this.moneyFormat);
    }

    // Fallback formatting
    const dollars = (cents / 100).toFixed(2);
    return this.moneyFormat.replace('{{amount}}', dollars).replace('{{amount_no_decimals}}', Math.round(cents / 100));
  }

  getCurrentQuantity() {
    return this.currentQuantity;
  }

  getCurrentPaidQuantity() {
    return this.currentPaidQuantity;
  }

  getCurrentVariantId() {
    return this.currentVariantId;
  }
}

// Initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!window.bundleSelectorInstance) {
      window.bundleSelectorInstance = new BundleSelector();
    }
  });
} else {
  if (!window.bundleSelectorInstance) {
    window.bundleSelectorInstance = new BundleSelector();
  }
}

// Reinitialize on section load (theme editor)
document.addEventListener('shopify:section:load', function() {
  window.bundleSelectorInstance = new BundleSelector();
});
